import {
  ADVERTISER_ROLE,
  DESIGNER_ROLE,
  MARKETER_ROLE,
  ROLES_ALLOWED,
  VIEWER_ROLE,
} from "./constants";
import {
  ADVERTISER_ROLE_TITLE_EXPLANATION,
  DESIGNER_ROLE_TITLE_EXPLANATION,
  MARKETER_ROLE_TILE_EXPLANATION,
  ROLE_CAPABILITY_CAN_ACCESS_SETTINGS_ACCOUNTS,
  ROLE_CAPABILITY_CAN_CREATE_CAMPAIGN,
  ROLE_CAPABILITY_CAN_EDIT_BILLING,
  ROLE_CAPABILITY_CAN_EDIT_BRAND_SETUP,
  ROLE_CAPABILITY_CAN_EDIT_CAMPAIGN,
  ROLE_CAPABILITY_CAN_EDIT_GALLERY,
  ROLE_CAPABILITY_CAN_POST,
  ROLE_CAPABILITY_CAN_MANAGE_USERS,
  ROLE_CAPABILITY_CAN_USE_CAMPAIGN_AGENT,
  VIEWER_CAPABILITY_VIEW_CALENDAR,
  VIEWER_CAPABILITY_VIEW_CAMPAIGNS,
  VIEWER_CAPABILITY_VIEW_CONTENT,
  VIEWER_CAPABILITY_VIEW_REPORTS,
  VIEWER_ROLE_TITLE_EXPLANATION,
} from "../i18n/keysTranslations";

export const VIEWER_CAPABILITY_TOOLTIP_KEYS = [
  VIEWER_CAPABILITY_VIEW_CAMPAIGNS,
  VIEWER_CAPABILITY_VIEW_CONTENT,
  VIEWER_CAPABILITY_VIEW_CALENDAR,
  VIEWER_CAPABILITY_VIEW_REPORTS,
];

const FULL_CAMPAIGN_ACCESS = {
  canEditBilling: true,
  canManageUsers: true,
  canAccessSettingsAccounts: true,
  canCreateCampaign: true,
  canEditCampaign: true,
  canUseCampaignAgent: true,
  canEditGallery: true,
  canPost: true,
  canEditBrandSetup: true,
};

const MARKETER_CAPABILITIES = {
  ...FULL_CAMPAIGN_ACCESS,
  canManageUsers: false,
  canEditBilling: false,
};

const DESIGNER_CAPABILITIES = {
  canManageUsers: false,
  canAccessSettingsAccounts: false,
  canCreateCampaign: false,
  canEditCampaign: false,
  canUseCampaignAgent: false,
  canEditGallery: true,
  canPost: true,
  canEditBrandSetup: true,
  canEditBilling: false,
};

const VIEWER_CAPABILITIES = {
  canManageUsers: false,
  canAccessSettingsAccounts: false,
  canCreateCampaign: false,
  canEditCampaign: false,
  canUseCampaignAgent: false,
  canEditGallery: false,
  canPost: false,
  canEditBrandSetup: false,
  canEditBilling: false,
};

export const ADS_ROLE_CAPABILITIES = {
  [ADVERTISER_ROLE]: FULL_CAMPAIGN_ACCESS,
  [MARKETER_ROLE]: MARKETER_CAPABILITIES,
  [DESIGNER_ROLE]: DESIGNER_CAPABILITIES,
  [VIEWER_ROLE]: VIEWER_CAPABILITIES,
};

export const hasRoleCapability = (role, capability) => {
  const capabilities = ADS_ROLE_CAPABILITIES[role];
  if (!capabilities) {
    return true;
  }
  return capabilities[capability] ?? false;
};

export const getRoleCapabilities = (role) => {
  const capabilities = ADS_ROLE_CAPABILITIES[role];
  if (!capabilities) {
    return { ...FULL_CAMPAIGN_ACCESS };
  }
  return { ...capabilities };
};

export const ROLES_WITH_USERS_MENU = ROLES_ALLOWED.filter((role) =>
  hasRoleCapability(role, "canManageUsers")
);

export const ROLES_WITH_BILLING = ROLES_ALLOWED.filter((role) =>
  hasRoleCapability(role, "canEditBilling")
);

export const ROLE_CAPABILITY_KEYS = Object.keys(FULL_CAMPAIGN_ACCESS);

export const ROLE_CAPABILITY_I18N_KEYS = {
  canManageUsers: ROLE_CAPABILITY_CAN_MANAGE_USERS,
  canAccessSettingsAccounts: ROLE_CAPABILITY_CAN_ACCESS_SETTINGS_ACCOUNTS,
  canCreateCampaign: ROLE_CAPABILITY_CAN_CREATE_CAMPAIGN,
  canEditCampaign: ROLE_CAPABILITY_CAN_EDIT_CAMPAIGN,
  canUseCampaignAgent: ROLE_CAPABILITY_CAN_USE_CAMPAIGN_AGENT,
  canEditGallery: ROLE_CAPABILITY_CAN_EDIT_GALLERY,
  canPost: ROLE_CAPABILITY_CAN_POST,
  canEditBrandSetup: ROLE_CAPABILITY_CAN_EDIT_BRAND_SETUP,
  canEditBilling: ROLE_CAPABILITY_CAN_EDIT_BILLING,
};

export const ROLE_TITLE_I18N_KEYS = {
  [ADVERTISER_ROLE]: ADVERTISER_ROLE_TITLE_EXPLANATION,
  [MARKETER_ROLE]: MARKETER_ROLE_TILE_EXPLANATION,
  [DESIGNER_ROLE]: DESIGNER_ROLE_TITLE_EXPLANATION,
  [VIEWER_ROLE]: VIEWER_ROLE_TITLE_EXPLANATION,
};

export const getEnabledRoleCapabilityLabels = ({ role, t }) => {
  const capabilities = getRoleCapabilities(role);

  return ROLE_CAPABILITY_KEYS.filter((key) => capabilities[key]).map((key) =>
    t(ROLE_CAPABILITY_I18N_KEYS[key])
  );
};

export const getRoleCapabilityTooltipLabels = ({ role, t }) => {
  if (role === VIEWER_ROLE) {
    return VIEWER_CAPABILITY_TOOLTIP_KEYS.map((key) => t(key));
  }

  const capabilities = getRoleCapabilities(role);
  const labels = getEnabledRoleCapabilityLabels({ role, t });

  if (!capabilities.canEditCampaign) {
    return [t(VIEWER_CAPABILITY_VIEW_CAMPAIGNS), ...labels];
  }

  return labels;
};
